/**
 * ADR 215 — decode an uploaded plan image into themed pixels, once, for whoever
 * needs to paint it: the /live minimap and the alignment editor both go through
 * here so a plan can never look different in the two places.
 *
 * Not in `planRaster.ts` because this touches the DOM (canvas decode); that file
 * stays pure so its maths can be tested without a browser.
 */

import { fitPlanSize, themePlanPixels } from "./planRaster";

export interface DecodedPlan {
  /** Themed pixels, ready to blit at `width` × `height`. */
  image: ImageData;
  width: number;
  height: number;
  /** The image's own pixel size, before the decode cap — the georeference's
   *  `resolution` is stated per SOURCE pixel, so callers need this to rescale. */
  sourceWidth: number;
  sourceHeight: number;
}

export async function decodeThemedPlan(blob: Blob, maxEdge: number): Promise<DecodedPlan | null> {
  if (typeof createImageBitmap !== "function") return null;
  const bitmap = await createImageBitmap(blob);
  const fit = fitPlanSize(bitmap.width, bitmap.height, maxEdge);
  const scratch = document.createElement("canvas");
  scratch.width = fit.width;
  scratch.height = fit.height;
  const ctx = scratch.getContext("2d");
  if (!ctx) return null;
  ctx.drawImage(bitmap, 0, 0, fit.width, fit.height);
  const image = ctx.getImageData(0, 0, fit.width, fit.height);
  themePlanPixels(image.data);
  const out: DecodedPlan = {
    image,
    width: fit.width,
    height: fit.height,
    sourceWidth: bitmap.width,
    sourceHeight: bitmap.height,
  };
  bitmap.close?.();
  return out;
}
